import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

export interface SubscriptionTier {
  key: string;
  label: string;
}

export interface OrgFilterOptions {
  categories: string[];
  /** Active tier keys, catalog order. */
  subscriptions: string[];
  /** The same tiers with display labels — what every tier picker shows. */
  subscriptionTiers: SubscriptionTier[];
  /** key → label for EVERY catalog tier, retired ones included (display only). */
  tierLabels: Record<string, string>;
}

/**
 * Organization filter options from the subscription_types catalog — the one
 * source for the Create / Edit / bulk tier pickers and the list filter.
 */
export function useOrgFilterOptions({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['admin-accounts', 'org-filter-options'],
    queryFn: async () => {
      const response = await api.get('/admin/accounts/organizations/filter-options');
      const data = response.data.data as Partial<OrgFilterOptions>;
      const subscriptions = data.subscriptions ?? [];
      return {
        categories: data.categories ?? [],
        subscriptions,
        subscriptionTiers: data.subscriptionTiers ?? subscriptions.map((key) => ({ key, label: key })),
        tierLabels: data.tierLabels ?? {},
      } as OrgFilterOptions;
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Display label for a tier key ('none' → 'No Subscription', a retired
 * 'not_subscribed' → 'Not Subscribed'); the key itself when unknown.
 */
export function tierLabel(options: Pick<OrgFilterOptions, 'tierLabels' | 'subscriptionTiers'> | undefined, key: string): string {
  return options?.tierLabels?.[key] ?? options?.subscriptionTiers?.find((tier) => tier.key === key)?.label ?? key;
}
